import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, SpinnerGap, CheckCircle, ShieldCheck } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast({ title: 'Password updated', description: 'You can now sign in with your new password.' });
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
        <div className="max-w-md w-full bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] text-center space-y-4">
          <h1 className="text-2xl font-black text-neutral-900 dark:text-white">Invalid Reset Link</h1>
          <p className="text-neutral-600 dark:text-neutral-400">This link is missing its reset token. Request a new one below.</p>
          <Link to="/forgot-password" className="inline-block text-[#2B4C6F] font-bold hover:underline" data-testid="request-new-link">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center py-12 px-4 transition-colors duration-300" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-neutral-900 dark:text-white">Choose a New Password</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">Minimum 8 characters</p>
        </div>

        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="w-20 h-20 mx-auto bg-emerald-500 rounded-2xl flex items-center justify-center border-2 border-black shadow-[3px_3px_0px_#000]">
                <CheckCircle size={40} weight="bold" className="text-white" />
              </div>
              <h2 className="text-xl font-black text-neutral-900 dark:text-white">Password updated!</h2>
              <p className="text-neutral-600 dark:text-neutral-400">Redirecting you to sign in...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="new-password" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">New Password</Label>
                <div className="relative">
                  <Lock size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl focus:ring-4 focus:ring-[#2B4C6F]/20 dark:bg-neutral-700 dark:text-white"
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-password" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Confirm Password</Label>
                <div className="relative">
                  <ShieldCheck size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter new password"
                    required
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl focus:ring-4 focus:ring-[#2B4C6F]/20 dark:bg-neutral-700 dark:text-white"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm font-bold text-red-600" data-testid="reset-password-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-16 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <><SpinnerGap size={24} weight="bold" className="animate-spin" /> Updating...</>
                ) : (
                  <><Lock size={24} weight="bold" /> Update Password</>
                )}
              </button>

              <Link
                to="/login"
                className="w-full h-12 flex items-center justify-center gap-2 text-neutral-600 dark:text-neutral-400 font-bold hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft size={20} weight="bold" /> Back to login
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
